import { useState, useMemo, useEffect } from "react";
import { Container, Box, CircularProgress } from "@mui/material";
import ReactQuill from 'react-quill';
import { marked } from 'marked';
import { useNavigate } from 'react-router-dom';
import TurndownService from 'turndown';
import styled from "@emotion/styled";
import StyledButton from "components/button/StyledButton";
import MessageModal from "components/modal/MessageModal";
import 'react-quill/dist/quill.snow.css';

interface PolicyEditorProps {
  title: string;
  initialText?: string;
  onUpdate: (markdown: string) => Promise<void>;
  isLoading?: boolean;
}

const PolicyEditor = ({ title, initialText = '', onUpdate, isLoading }: PolicyEditorProps) => {
  const [editorValue, setEditorValue] = useState<string>('');
  const [isSaved, setIsSaved] = useState<boolean>(true);
  const [showAcknowledgement, setShowAcknowledgement] = useState<boolean>(false);

  const navigate = useNavigate();
  const turndownService = new TurndownService();

  const fonts = ['helvetica-neue', 'sans-serif', 'serif', 'monospace'];
  const Font = ReactQuill.Quill.import('formats/font');
  Font.whitelist = fonts;

  const markdownContent = useMemo(() =>
    turndownService.turndown(editorValue),
    [editorValue]
  );

  const modules = {
    toolbar: [
      [{ 'font': fonts }],
      [{ 'header': [1, 2, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'list': 'ordered' }, { 'list': 'bullet' }],
      [{ 'align': [] }],
      ['link'],
      [{ 'color': [] }, { 'background': [] }],
      ['clean'],
    ]
  };

  const handleCancel = () => {
    navigate('/dashboard');
  }

  const handleUpdate = async () => {
    try {
      await onUpdate(markdownContent);
      setIsSaved(true);
      setShowAcknowledgement(true);
    } catch (e) {
      console.error(`Update ${title} error:`, e);
    }
  }

  const handleOnChange = (value: string) => {
    setEditorValue(value);
    if (isSaved) {
      setIsSaved(false);
    }
  }

  useEffect(() => {
    if (initialText) {
      const htmlText = marked(initialText);
      setEditorValue(htmlText as string);
    }
  }, [initialText]);

  return (
    <div>
      <StyledContainer>
        <div className="editor-container">
          <h2>{title}</h2>
          <ReactQuill
            value={editorValue}
            onChange={handleOnChange}
            modules={modules}
          />
          <Box sx={{
            display: 'flex',
            maxWidth: '20vw',
            marginTop: '20px',
            gap: '10px'
          }}>
            <StyledButton
              label={!isSaved ? "Update" : "Saved"}
              type='filled'
              onClick={handleUpdate}
              disabled={isLoading || isSaved}
              endAdornment={isLoading && <CircularProgress size={16} sx={{ color: '#fff' }} />}
            />
            <StyledButton
              label="Cancel"
              type='filled'
              onClick={handleCancel}
            />
          </Box>
        </div>
        <MessageModal
          open={showAcknowledgement}
          onClose={() => setShowAcknowledgement(false)}
          message={showAcknowledgement ? `${title} has been successfully updated.` : null}
          buttonText="Ok"
        />
      </StyledContainer>
    </div>
  );
};

const StyledContainer = styled(Container)({
  "& .editor-container": {
    maxWidth: '100%',
    width: '100%',
    height: '100%',
    background: 'white',
    boxShadow: '0px 1px 8px rgba(0, 0, 0, 0.12)',
    borderRadius: 5,
    padding: 20,
    paddingLeft: 20,
    paddingTop: 1,
    marginTop: 20
  },
  "& .sectionTitle": {
    textDecoration: "underline",
    fontWeight: "bold",
  },
});

export default PolicyEditor;